import React, { useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import { isValidEmail } from "utils/Validation/isValidEmail";
import { getErrorMessage } from "utils/errorHelpers";
import { LoadablePage } from "shared/LoadablePage";
import { CountryStatus, type BillingAccountDto, type CountryDto, type QuoteTaxRecalculationDto } from "client/api-v2";
import { getCountries } from "client/api/CountriesApi";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { attentionPurpose } from "../attention";
import { liveQuoteTaxAwaitsPushNotice, liveQuoteTaxNotice } from "../liveQuoteTax";
import { paymentTermOptions } from "../paymentTerms";
import { createBillingAccount, updateBillingAccount } from "../services/billingAccountService";
import {
  collectErrorMessages,
  type BillingAccountFormValues,
  defaultBillingAccountFormValues,
  toBillingAccountFormValues,
  toCreateBillingAccountDto,
  toUpdateBillingAccountDto,
} from "./billingAccountForm";

interface BillingAccountDialogProps {
  open: boolean;
  billingAccount?: BillingAccountDto;
  liveOrderCount?: number;
  onCancel: () => void;
  onSaved: (account: BillingAccountDto, taxRecalculations?: QuoteTaxRecalculationDto[]) => void;
}

const formId = "billing-account-form";

const zuoraAccountNumberMaxLength = 255;
const zuoraAccountNumberLockedMessage = "A Zuora account number can only be set once";

// Shared so the inactive marker reads the same whether choosing or already chosen.
function CountryOptionContent({ country }: { country: CountryDto }) {
  return (
    <>
      {country.name}
      {country.status !== CountryStatus.Active && (
        <Chip label="Inactive" size="small" variant="outlined" color="warning" sx={{ ml: 1 }} />
      )}
    </>
  );
}

const taxJurisdictionFields = ["countryCode", "state", "city", "postalCode", "zuoraAccountNumber"] as const;

function requiredValidator(message: string) {
  return (value: string) => value.trim() !== "" || message;
}

function BillingAccountDialogInner({
  billingAccount,
  liveOrderCount = 0,
  onCancel,
  onSaved,
}: Omit<BillingAccountDialogProps, "open">) {
  const isEditing = !!billingAccount;
  const isZuoraAccountNumberSetOnceLocked = !!billingAccount?.zuoraAccountNumber;
  const [serverError, setServerError] = useState<string | undefined>();
  const { isAuthorized } = useRbacContext();
  const canSave = isAuthorized(RbacPolicies.BillingAccounts.ReadWrite);
  const { data: countries = [], loading: countriesLoading } = useAsyncAbortableRequest(getCountries);
  const sortedCountries = useMemo(() => [...countries].sort((a, b) => a.name.localeCompare(b.name)), [countries]);

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<BillingAccountFormValues>({
    defaultValues: billingAccount ? toBillingAccountFormValues(billingAccount) : defaultBillingAccountFormValues,
  });

  const isTaxExempt = watch("isTaxExempt");
  const jurisdictionDirty = taxJurisdictionFields.some((field) => dirtyFields[field]);
  const heldByZuora = watch("zuoraAccountNumber").trim() !== "";

  const onSubmit = async (values: BillingAccountFormValues) => {
    setServerError(undefined);
    try {
      if (isEditing) {
        const result = await updateBillingAccount(billingAccount.code, toUpdateBillingAccountDto(values));
        onSaved(result, result.taxRecalculations ?? []);
      } else {
        onSaved(await createBillingAccount(toCreateBillingAccountDto(values)));
      }
    } catch (e) {
      setServerError(getErrorMessage(e));
    }
  };

  const errorMessages = [...(serverError ? [serverError] : []), ...collectErrorMessages(errors)];

  if (countriesLoading) {
    return (
      <Dialog open={true} onClose={() => !isSubmitting && onCancel()} maxWidth="sm" fullWidth>
        <LoadablePage loading={countriesLoading}></LoadablePage>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={() => !isSubmitting && onCancel()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? "Edit Billing Account" : "Create Billing Account"}</DialogTitle>
      <DialogContent>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2.5} sx={{ pt: 1 }}>
            {errorMessages.length > 0 && (
              <Grid size={12}>
                <Alert severity="error">
                  {errorMessages.map((message) => (
                    <div key={message}>{message}</div>
                  ))}
                </Alert>
              </Grid>
            )}

            <Grid size={12}>
              <Controller
                name="legalName"
                control={control}
                rules={{ validate: requiredValidator("Legal name is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Legal name"
                    placeholder="e.g. Globex Industries Ltd"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message ?? "Legal entity name; appears on invoices"}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="commonName"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    label="Common name"
                    placeholder="e.g. Globex"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="crmCustomerId"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    label="CRM Account reference"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="zuoraAccountNumber"
                control={control}
                rules={{
                  maxLength: { value: zuoraAccountNumberMaxLength, message: "Zuora Account reference is too long" },
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <Tooltip title={isZuoraAccountNumberSetOnceLocked ? zuoraAccountNumberLockedMessage : ""}>
                    <TextField
                      {...rest}
                      inputRef={ref}
                      fullWidth
                      disabled={isZuoraAccountNumberSetOnceLocked}
                      label="Zuora Account reference"
                      placeholder="e.g. A00011201"
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  </Tooltip>
                )}
              />
            </Grid>

            <Grid size={12}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <Typography variant="overline" color="text.secondary">
                  Attention (invoice addressee)
                </Typography>
                <Tooltip title={attentionPurpose}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="attentionFirstName"
                control={control}
                rules={{ validate: requiredValidator("Attention first name is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Attention first name"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="attentionLastName"
                control={control}
                rules={{ validate: requiredValidator("Attention last name is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Attention last name"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="attentionEmail"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value.trim() === "") {
                      return "Attention email is required";
                    }
                    return isValidEmail(value) || "Attention email must be a valid email address";
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Attention email"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="overline" color="text.secondary">
                Address
              </Typography>
            </Grid>

            <Grid size={12}>
              <Controller
                name="countryCode"
                control={control}
                rules={{ validate: requiredValidator("Country is required") }}
                render={({ field: { ref, onChange, value, ...rest }, fieldState }) => (
                  <Autocomplete<CountryDto>
                    {...rest}
                    options={sortedCountries}
                    getOptionLabel={(country) => country.name}
                    isOptionEqualToValue={(option, selected) => option.twoDigitIsoCode === selected.twoDigitIsoCode}
                    value={sortedCountries.find((country) => country.twoDigitIsoCode === value) ?? null}
                    onChange={(_, selected) => onChange(selected?.twoDigitIsoCode ?? "")}
                    renderOption={(liProps, country) => (
                      <li {...liProps}>
                        <CountryOptionContent country={country} />
                      </li>
                    )}
                    renderValue={(country) => <CountryOptionContent country={country} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        ref={ref}
                        required
                        label="Country"
                        slotProps={{
                          ...params.slotProps,
                          inputLabel: { ...params.slotProps?.inputLabel, shrink: true },
                        }}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="state"
                control={control}
                rules={{ validate: requiredValidator("State / Region is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="State / Region"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="city"
                control={control}
                rules={{ validate: requiredValidator("City is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="City"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="address1"
                control={control}
                rules={{ validate: requiredValidator("Street is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Street"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="address2"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    label="Street 2"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="postalCode"
                control={control}
                rules={{ validate: requiredValidator("Postcode is required") }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    required
                    label="Postcode"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="overline" color="text.secondary">
                Billing
              </Typography>
            </Grid>

            <Grid size={12}>
              <Controller
                name="paymentTerm"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    select
                    fullWidth
                    required
                    label="Payment term"
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText="When payment falls due on this account's invoices"
                  >
                    {paymentTermOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="taxId"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    label="Tax ID / VAT / ABN"
                    placeholder="e.g. GB 123 4567 89"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="overline" color="text.secondary">
                Tax exemption
              </Typography>
            </Grid>

            <Grid size={12}>
              <Controller
                name="isTaxExempt"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          // The certificate fields unmount when the box is unticked, and react-hook-form keeps
                          // errors for unmounted fields, so a stale error would sit in the banner until the next save.
                          if (!e.target.checked) clearErrors("taxExemptionCertificateId");
                        }}
                      />
                    }
                    label="Tax exempt"
                  />
                )}
              />
            </Grid>

            {isTaxExempt && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="taxExemptionCertificateId"
                    control={control}
                    rules={{
                      validate: requiredValidator(
                        "A tax exempt billing account requires a tax exemption certificate id"
                      ),
                    }}
                    render={({ field: { ref, ...rest }, fieldState }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        fullWidth
                        required
                        label="Certificate ID"
                        placeholder="e.g. ST-4 (05-12, R-15)"
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="taxExemptionCertificateType"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        fullWidth
                        label="Certificate type"
                        placeholder="e.g. ST-4"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="taxExemptionJurisdiction"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        fullWidth
                        label="Issuing jurisdiction"
                        placeholder="e.g. New Jersey"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="taxExemptionEntityUseCode"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        fullWidth
                        label="Entity/use code"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="taxExemptionDescription"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        fullWidth
                        label="Description"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Controller
                  name="isReseller"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title={isEditing ? "Whether an account is a reseller can only be set on creation" : ""}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            disabled={isEditing}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="This account is a reseller"
                      />
                    </Tooltip>
                  )}
                />
                <Box sx={{ pl: 4.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Reseller accounts appear in quoting and ordering flows. Keep this list curated — check for an
                    existing account for this reseller entity first.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      {jurisdictionDirty && liveOrderCount > 0 && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="info">
            {heldByZuora ? liveQuoteTaxAwaitsPushNotice(liveOrderCount) : liveQuoteTaxNotice("Saving", liveOrderCount)}
          </Alert>
        </Box>
      )}
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !canSave || countriesLoading}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function BillingAccountDialog({ open, ...props }: BillingAccountDialogProps) {
  if (open) {
    return <BillingAccountDialogInner {...props} />;
  }
  return null;
}
